import { createAsync, query, useParams } from "@solidjs/router";
import { createMemo, For, Show } from "solid-js";
import { CertificateState, formattedTimestamp, RIASRecordType } from "~/lib";
import { getByUser } from "~/_server/dynamoDB";

import "~/_assets/admin.css";

const projectsQuery = query( async ( userId: number ) => {
	"use server";

	return await getByUser( RIASRecordType.PROJECT, userId, -1 );
}, "getCertifierProjects" );

const projectCertificatesQuery = query( async ( userId: number ) => {
	"use server";

	return await getByUser( RIASRecordType.CERTIFICATE, userId, -1 );
}, "getCertifierProjectCertificates" );

export const CertifierProjects = () => {
	const { id } = useParams();
	const userId = parseInt( id ?? "0" );

	const projects = createAsync( () => projectsQuery( userId ) );
	const certificates = createAsync( () => projectCertificatesQuery( userId ) );

	// A certificate points at its project with parent = the project's sk. The
	// project's own certificateCount field isn't kept up to date, so count the
	// certificate records themselves. Stages and amendments are records of their
	// own, so they count too; drafts are tallied separately as they are often
	// abandoned attempts.
	const counts = createMemo( () => {
		const byProject = new Map<string, { total: number; drafts: number }>();

		for ( const c of certificates()?.items ?? [] ) {
			if ( !c.parent ) continue;

			const entry = byProject.get( c.parent ) ?? { total: 0, drafts: 0 };

			entry.total++;

			if ( c.status === CertificateState.DRAFT ) entry.drafts++;

			byProject.set( c.parent, entry );
		}

		return byProject;
	} );

	return (
		<table id="projects-table" class="table-fixed w-full border-collapse text-[13px]" aria-label="Projects list">
			<thead>
				<tr>
					<th class="text-left w-28">Created</th>
					<th class="text-left">Project</th>
					<th class="text-right w-20">Plots</th>
					<th class="text-right w-36">Certificates</th>
				</tr>
			</thead>
			<tbody>
				<Show when={projects()}>
					<For
						each={projects()?.items}
						fallback={
							<tr>
								<td colspan="4" class="px-3 py-3 border-b border-border text-gray-500">No projects.</td>
							</tr>
						}>
						{( p: any ) => {
							const count = () => counts().get( p.sk );

							return (
								<tr>
									<td class="px-3 py-1.75 border-b border-border align-middle whitespace-nowrap">
										{formattedTimestamp( p.created ?? 0 )}
									</td>
									<td class="px-3 py-1.75 border-b border-border align-middle truncate" title={p.name}>
										<Show when={p.name?.trim()} fallback={<span class="text-gray-400 italic">(unnamed)</span>}>
											{p.name}
										</Show>
									</td>
									<td class="px-3 py-1.75 border-b border-border align-middle text-right tabular-nums">
										{p.plots?.length ?? 0}
									</td>
									<td class="px-3 py-1.75 border-b border-border align-middle text-right tabular-nums">
										{count()?.total ?? 0}
										<Show when={count()?.drafts}>
											<span class="ml-1 text-[11px] text-gray-500">({count()?.drafts} draft)</span>
										</Show>
									</td>
								</tr>
							);
						}}
					</For>
				</Show>
			</tbody>
		</table>
	);
}
